import { apiClient } from './client';
import type { User } from '@/types';

export type CallMode = 'VOICE' | 'VIDEO';
export type CallStatus =
  | 'RINGING'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'MISSED'
  | 'ENDED'
  | 'FAILED';

export type CallPeer = Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl' | 'status'>;

export interface Call {
  id: string;
  callerId: string;
  calleeId: string;
  mode: CallMode;
  status: CallStatus;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  endReason: string | null;
  createdAt: string;
  updatedAt: string;
  caller: CallPeer;
  callee: CallPeer;
}

export interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

export interface CallHistoryPage {
  data: Call[];
  nextCursor: string | null;
}

export const callsApi = {
  async getIceServers() {
    const res = await apiClient.get<{ iceServers: IceServer[] }>('/api/calls/ice');
    return res.data.iceServers;
  },

  async getHistory(params: { cursor?: string; limit?: number; filter?: 'all' | 'missed' } = {}) {
    const res = await apiClient.get<CallHistoryPage>('/api/calls/history', { params });
    return res.data;
  },

  async getActive() {
    const res = await apiClient.get<{ call: Call | null }>('/api/calls/active');
    return res.data.call;
  },

  async getById(callId: string) {
    const res = await apiClient.get<Call>(`/api/calls/${callId}`);
    return res.data;
  },
};

// Format duration as mm:ss (or hh:mm:ss for long calls).
export function formatCallDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

// Human-readable short label for history rows.
export function callStatusLabel(call: Call, currentUserId: string): string {
  const outgoing = call.callerId === currentUserId;
  switch (call.status) {
    case 'RINGING':
      return outgoing ? 'Исходящий, вызов...' : 'Входящий, вызов...';
    case 'ACCEPTED':
      return outgoing ? 'Исходящий, в разговоре' : 'Входящий, в разговоре';
    case 'ENDED':
      return outgoing ? 'Исходящий' : 'Входящий';
    case 'MISSED':
      return outgoing ? 'Не отвечен' : 'Пропущенный';
    case 'REJECTED':
      return outgoing ? 'Отклонён' : 'Отклонён вами';
    case 'CANCELLED':
      return outgoing ? 'Отменён вами' : 'Отменён';
    case 'FAILED':
      return 'Ошибка соединения';
    default:
      return call.status;
  }
}
